import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Card, Container, useTheme } from "@mui/material";
import AccountingTable from "./AccountingTable";
import { Helmet } from "react-helmet-async";
import { findFund, findFundAbbById, findStrategy } from "../../component/Methods";
import AccountingDialog from "./AccountingDialog";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { useAccountingState } from "../../hooks/useOptimizedState";
import { addActiveRequests } from "../../Redux/Reducers/uiSlice";
import {
  SYNC_SEAMLESS_ACTIVE_REQUEST,
  SYNC_SEAMLESS_STARTED_REQUEST,
} from "../../Redux/Reducers/accountingSlice";
import { updateLoading, updateLoadingMsg } from "../../Redux/Reducers/uiSlice";

const SYNC_ADDEPAR_REQUEST = "fetch_addepar_portfolio";
const SYNC_SCHWAB_REQUEST = "sma_sync";
const SYNC_SWAP_REQUEST = "swap_sync";

const Accounting = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const activeRequests = useSelector((state) => state.ui?.activeRequests || []);
  const isSyncing =
    activeRequests.includes(SYNC_SEAMLESS_ACTIVE_REQUEST) ||
    activeRequests.includes(SYNC_SEAMLESS_STARTED_REQUEST);
  const isAddeparSyncing = activeRequests.includes(SYNC_ADDEPAR_REQUEST);
  const isSchwabSyncing = activeRequests.includes(SYNC_SCHWAB_REQUEST);
  const isSwapSyncing = activeRequests.includes(SYNC_SWAP_REQUEST);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);

  const { accountingData, funds, strategies } = useAccountingState();

  // Process accounting data similar to the original implementation
  const processedData = useMemo(() => {
    const fund = accountingData?.[0]?.funds;
    if (!fund) return [];

    return fund.map((row) => {
      const foundFund = findFund(row?.fund, funds);
      const strategyAUMsData = Object.keys(row?.strategyAUMs || {})?.map(
        (strategyId) => {
          return {
            strategyName: findStrategy(strategyId, strategies),
            strategyAUM: row?.strategyAUMs[strategyId],
            strategyId: strategyId,
          };
        }
      );

      const filteredStrategyAUMsData = strategyAUMsData?.filter(
        (item) => item?.strategyName && item?.strategyAUM !== undefined
      );

      const sortedStrategyAUMsData = filteredStrategyAUMsData
        ?.slice()
        ?.sort((a, b) => a?.strategyName?.localeCompare(b?.strategyName));

      return {
        ...row,
        name: foundFund,
        abbreviation: findFundAbbById(row?.fund, funds) ?? row?.abbreviation ?? "-",
        strategyAUMs: sortedStrategyAUMsData,
        id: row?.fund || Math.random().toString(),
      };
    });
  }, [accountingData, funds, strategies]);

  const handleEdit = (record) => {
    setRecordForEdit(record);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setRecordForEdit(null);
  };

  const handleSync = () => {
    if (isSyncing) return;
    dispatch(addActiveRequests(SYNC_SEAMLESS_ACTIVE_REQUEST));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Syncing Data from Seamless..."));
    sendWebSocketMessage(
      JSON.stringify({ action: SYNC_SEAMLESS_ACTIVE_REQUEST })
    );
  };

  const handleAddeparSync = () => {
    if (isAddeparSyncing) return;
    dispatch(addActiveRequests(SYNC_ADDEPAR_REQUEST));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Syncing Data from Addepar..."));
    sendWebSocketMessage(JSON.stringify({ action: SYNC_ADDEPAR_REQUEST }));
  };

  const handleSchwabSync = () => {
    if (isSchwabSyncing) return;
    dispatch(addActiveRequests(SYNC_SCHWAB_REQUEST));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Syncing SMA data from Schwab..."));
    sendWebSocketMessage(JSON.stringify({ action: SYNC_SCHWAB_REQUEST }));
  };

  const handleSwapSync = () => {
    if (isSwapSyncing) return;
    dispatch(addActiveRequests(SYNC_SWAP_REQUEST));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Syncing swap data..."));
    sendWebSocketMessage(JSON.stringify({ action: SYNC_SWAP_REQUEST }));
  };

  const handleSyncAll = () => {
    // Start a combined sync: Seamless + Addepar
    // Skip if both are already in progress.
    if (isSyncing && isAddeparSyncing) return;

    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Syncing accounting data..."));

    // Always send both requests; only addActiveRequests if not already marked active.
    if (!isSyncing) {
      dispatch(addActiveRequests(SYNC_SEAMLESS_ACTIVE_REQUEST));
    }
    sendWebSocketMessage(
      JSON.stringify({ action: SYNC_SEAMLESS_ACTIVE_REQUEST })
    );

    if (!isAddeparSyncing) {
      dispatch(addActiveRequests(SYNC_ADDEPAR_REQUEST));
    }
    sendWebSocketMessage(JSON.stringify({ action: SYNC_ADDEPAR_REQUEST }));
  };

  return (
    <>
      <Helmet>
        <title>Accounting | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode === "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Accounting
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Fund accounting overview with strategy allocations
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <AccountingTable
              accountingData={processedData}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              onEdit={handleEdit}
              onSyncAll={handleSyncAll}
              onSync={handleSync}
              onAddeparSync={handleAddeparSync}
              onSchwabSync={handleSchwabSync}
              onSwapSync={handleSwapSync}
              isSyncing={isSyncing}
              isAddeparSyncing={isAddeparSyncing}
              isSchwabSyncing={isSchwabSyncing}
              isSwapSyncing={isSwapSyncing}
            />
          </Card>
        </Container>

        {/* Edit Dialog */}
        <AccountingDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          record={recordForEdit}
        />
      </Box>
    </>
  );
};

export default Accounting;
